import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ComposedChart, Line, Legend, ReferenceLine,
} from 'recharts';
import {
  Wallet, TrendingUp, Receipt, Percent, Target, Coins, Settings, Plus, Pencil, Trash2,
  Power, RefreshCw, AlertTriangle, Info, Store as StoreIcon,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import { IStoreResponse } from '../interfaces/responses/StoreResponse.interface';
import { KpiCard } from '../components/smart-analytics/KpiCard';
import { DateRangeFilter } from '../components/smart-analytics/DateRangeFilter';
import './NetProfit.page.css';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Category { key: string; label: string; icon: string; hint: string }
type Frequency = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'once';

interface ExpenseItem {
  _id: string; name: string; category: string; amount: number; frequency: Frequency;
  date?: string | null; startDate?: string | null; endDate?: string | null;
  active: boolean; notes?: string | null; monthlyEquivalent: number; costInPeriod: number;
}
interface ProfitSettings {
  costPerReturn: number; costPerFailed: number; taxRatePct: number; dateBasis: 'date' | 'order';
}
interface Summary {
  period: { from: string; to: string; days: number };
  settings: ProfitSettings;
  sales: {
    revenue: number; customerPaid: number; delivered: number; returned: number; failed: number; canceled: number;
    cogs: number; transport: number; storage: number; packaging: number; productCosts: number;
    jumiaFees: number; returnCost: number; failedCost: number;
    unknownCostOrders: number; unknownCostPayout: number; grossProfitSA: number; grossProfit: number;
  };
  marjan: {
    stores: number; sales: number; commissions: number; services: number; refunds: number; other: number; fees: number; payout: number;
    units: number; returnedUnits: number; orders: number; cogs: number; transport: number; storage: number; packaging: number; productCosts: number;
    knownPayout: number; profit: number; unknownCostSkus: number; unknownCostPayout: number;
  };
  combined: {
    jumiaProfit: number; marjanProfit: number; grossProfit: number; payout: number; customerPaid: number; fees: number;
    productCosts: number; cogs: number; logistics: number; orders: number; channels: { jumia: boolean; marjan: boolean };
  };
  byStore: { storeId: string; name: string; channel: 'jumia' | 'marjan'; delivered: number; revenue: number; grossProfit: number }[];
  expenses: {
    items: ExpenseItem[]; total: number; monthlyRunRate: number;
    byCategory: { category: string; label: string; icon: string; total: number; count: number }[];
  };
  net: {
    beforeTax: number; tax: number; afterTax: number; marginPct: number; perDeliveredOrder: number;
    grossPerOrder: number; expensesPerOrder: number; breakEvenOrders: number | null; dailyNet: number; monthlyRunRate: number;
    // orders still on the way (pending / ready to ship / shipped) and the result once they settle
    expected?: {
      jumia?: { orders: number; shipped: number; notShipped: number; value: number; profit: number; expectedDeliveries: number; rate: number; unknownCost: number };
      marjan?: { orders: number; atHub: number; notAtHub: number; value: number; profit: number; expectedDeliveries: number; rate: number; unknownCost: number };
      onTheWay: number; orders: number; shipped: number; notShipped: number; expectedDeliveries: number; shipRate: number; unknownCost: number; beforeTax: number; tax: number; afterTax: number; dailyNet: number };
    final?: boolean; finalOn?: string;
  };
  warnings: { type: string; text: string }[];
  categories: Category[];
}
interface VerdictPeriod {
  key: string; label: string; from: string; to: string; days: number;
  ordersPerDay: number; profitPerUnit: number | null; profitPerDay: number;
  deliveredProfitPerUnit: number | null; deliveredRatePct: number | null; cancelRatePct: number | null;
  placed: number; delivered: number; open: number; canceled: number; unknownCost: number;
}
interface Verdict {
  status: 'paying' | 'recovering' | 'flat' | 'not-paying' | 'too-early' | 'no-autopilot';
  headline: string; detail?: string; recommendation: string; notes?: string[];
  milestones?: { autoPilotStart: string; profitCheckSince: string };
  enrolledSkus?: number; periods: VerdictPeriod[]; nextCheckAt?: string;
}
const VERDICT_STYLE: Record<Verdict['status'], { label: string; bg: string; fg: string; icon: string }> = {
  'paying':       { label: 'Paying',        bg: '#DCFCE7', fg: '#166534', icon: '✅' },
  'recovering':   { label: 'Recovering',    bg: '#FEF9C3', fg: '#854D0E', icon: '🟡' },
  'flat':         { label: 'No change yet', bg: '#F1F5F9', fg: '#334155', icon: '⚪' },
  'not-paying':   { label: 'Not paying',    bg: '#FEE2E2', fg: '#991B1B', icon: '🔴' },
  'too-early':    { label: 'Too early',     bg: '#E0F2FE', fg: '#075985', icon: '⏳' },
  'no-autopilot': { label: 'Not enrolled',  bg: '#F1F5F9', fg: '#334155', icon: '—' },
};
interface MonthRow {
  month: string; label: string; partial: boolean; revenue: number; delivered: number;
  grossProfitSA: number; jumiaFees: number; jumiaProfit: number; marjanProfit: number; marjanPayout: number; marjanOrders: number;
  grossProfit: number; expenses: number; tax: number; net: number;
  expensesFullMonth?: number; daysInMonth?: number; daysElapsed?: number;
  onTheWay?: number; onTheWayOrders?: number; final?: boolean; finalOn?: string;
}
const fmtDay = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (d: Date) => d.toISOString().slice(0, 10);
const fmtMAD = (n: number) => `${Math.round(n || 0).toLocaleString('en-US')} MAD`;
const fmtK = (n: number) => Math.abs(n) >= 10000 ? `${Math.round(n / 1000)}k` : `${Math.round(n)}`;
const signed = (n: number) => `${n < 0 ? '−' : n > 0 ? '+' : ''}${fmtMAD(Math.abs(n))}`;
const FREQ_LABEL: Record<Frequency, string> = { daily: '/ day', weekly: '/ week', monthly: '/ month', yearly: '/ year', once: 'one-time' };
const C = { gross: '#2563EB', marjan: '#7C3AED', expenses: '#EA580C', pos: '#059669', neg: '#DC2626', total: '#334155', ink: '#0F172A' };
const isErr = (r: any) => !r || (typeof r === 'object' && 'error' in r);
const errMsg = (r: any) => r?.error?.message || 'Request failed';

const emptyForm = (category = 'other') => ({
  name: '', category, amount: '', frequency: 'monthly' as Frequency,
  date: fmt(new Date()), startDate: fmt(new Date()), endDate: '', notes: '', active: true,
});

interface TodayEstimate {
  available: boolean; reason?: string; date?: string;
  overallDeliveryRate: number; historyOrders: number;
  orders: number; canceled: number; unknownCost: number; expectedDeliveries: number;
  customerValue: number; expectedPayout: number; expectedProductCosts: number; expectedReturnCosts: number;
  grossProfit: number; expensesToday: number; beforeTax: number; tax: number; net: number;
  byProduct: { groupId: string; name: string; orders: number; deliveryRate: number; history: number; expectedDeliveries: number; expectedPayout: number; expectedProfit: number }[];
}

// ─── Break-even "energy bar" ─────────────────────────────────────────────────
// Fills with the month's gross profit (Jumia + Marjan) toward the whole month's
// expenses; red → orange → yellow while filling, green once they are covered.
const BreakEvenBar: FC<{ months: MonthRow[] }> = ({ months }) => {
  const rows = months.slice(-4);
  const [sel, setSel] = useState<string | null>(null);
  const [shown, setShown] = useState(0);
  const row = rows.find(r => r.month === sel) || rows[rows.length - 1];
  const goal = row ? (row.expensesFullMonth ?? row.expenses) : 0;
  const gross = row ? row.grossProfit : 0;
  const pct = goal > 0 ? (gross / goal) * 100 : (gross > 0 ? 100 : 0);
  // animate the fill on mount / month change
  useEffect(() => { setShown(0); const t = setTimeout(() => setShown(pct), 60); return () => clearTimeout(t); }, [row?.month, pct]);
  if (!row) return null;
  const covered = gross >= goal && goal > 0;
  const fillPct = Math.max(0, Math.min(100, covered ? 100 : shown));
  const level = covered ? 'green' : pct >= 66 ? 'yellow' : pct >= 33 ? 'orange' : 'red';
  const isCurrent = row.partial;
  const pace = isCurrent && row.daysInMonth ? Math.min(100, ((row.daysElapsed || 0) / row.daysInMonth) * 100) : null;
  const otw = row.final ? 0 : Math.max(0, row.onTheWay || 0);   // expected profit of the orders still on the way
  const ghost = !covered && otw > 0 && goal > 0 ? Math.min(100 - fillPct, (otw / goal) * 100) : 0;
  const breakMark = covered && gross > 0 ? (goal / gross) * 100 : null;  // where break-even sits once passed
  // typical gross profit per delivered order: last 3 complete months (a partial or holiday month distorts it)
  const full = months.filter(r => !r.partial && r.delivered > 0).slice(-3);
  const perOrder = full.length ? full.reduce((a, r) => a + r.grossProfit, 0) / full.reduce((a, r) => a + r.delivered, 0)
    : row.delivered > 0 ? row.grossProfit / row.delivered : 0;
  const left = Math.max(0, goal - gross);
  const ordersLeft = perOrder > 0 ? Math.ceil(left / perOrder) : null;
  const ahead = pace != null ? pct - pace : null;
  const monthName = new Date(row.month + '-01T12:00:00').toLocaleString('en-US', { month: 'long' });
  return (
    <div className={`np__eb np__eb--${level}`}>
      <div className="np__eb-top">
        <div className="np__eb-title">{covered ? '🏆' : '⚡'} {monthName} — covering this month's expenses{' '}
          {row.final ? <span className="np__final np__final--ok">final ✓</span> : <span className="np__final">⏳ still moving · final {fmtDay(row.finalOn)}</span>}
        </div>
        <div className="np__eb-tabs">
          {rows.map(r => (
            <button key={r.month} className={`np__eb-tab${r.month === row.month ? ' on' : ''}`} onClick={() => setSel(r.month)}>{r.label}</button>
          ))}
        </div>
      </div>
      <div className="np__eb-track">
        <div className={`np__eb-fill${covered ? '' : ' np__eb-fill--charging'}`} style={{ width: `${fillPct}%` }} />
        {ghost > 0 && <div className="np__eb-ghost" style={{ left: `${fillPct}%`, width: `${ghost}%` }} title="Expected profit from the orders still on the way" />}
        {pace != null && !covered && <div className="np__eb-pace" style={{ left: `${pace}%` }}><span>today</span></div>}
        {breakMark != null && <div className="np__eb-break" style={{ left: `${breakMark}%` }}><span>break-even</span></div>}
        <div className="np__eb-pct">{Math.round(pct)}%</div>
      </div>
      <div className="np__eb-line">
        <b>{fmtMAD(gross)}</b> / {fmtMAD(goal)}
        {covered
          ? <span className="np__eb-win"> · Break-even passed! <b>{signed(gross - goal)}</b> earned beyond {isCurrent ? 'this' : 'that'} month's expenses</span>
          : <> · <b>{fmtMAD(left)}</b> to go{ordersLeft != null ? ` ≈ ${ordersLeft.toLocaleString()} more delivered orders at your usual ${fmtMAD(perOrder)} profit per order` : ''}</>}
        {ahead != null && !covered && <span className={ahead >= 0 ? 'np__eb-ahead' : 'np__eb-behind'}> · {ahead >= 0 ? `ahead of pace by ${Math.round(ahead)} pts` : `behind pace by ${Math.round(-ahead)} pts`} (day {row.daysElapsed} of {row.daysInMonth})</span>}
        {otw > 0 && <span className="np__eb-ghosttxt"> · +{fmtMAD(otw)} expected from {row.onTheWayOrders} order{row.onTheWayOrders === 1 ? '' : 's'} still on the way (Jumia + Marjan)</span>}
      </div>
    </div>
  );
};

// ─── Small components ────────────────────────────────────────────────────────
const WaterfallTip: FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="np__tip">
      <b>{p.name}</b>
      <div><span>{p.kind === 'total' ? 'Total' : 'Amount'}</span><span>{p.kind === 'total' ? fmtMAD(p.end) : signed(p.delta)}</span></div>
      {p.kind !== 'total' && <div><span>Running total</span><span>{fmtMAD(p.end)}</span></div>}
      {p.hint && <div className="np__muted" style={{ display: 'block', marginTop: 4, maxWidth: 240 }}>{p.hint}</div>}
    </div>
  );
};

const MonthlyTip: FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const r: MonthRow = payload[0].payload;
  return (
    <div className="np__tip">
      <b>{label}{r.partial ? ' (month to date)' : ''}</b>
      <div><span>Revenue</span><span>{fmtMAD(r.revenue)}</span></div>
      <div><span>Orders</span><span>{r.delivered}</span></div>
      <div><span>Jumia profit</span><span>{fmtMAD(r.jumiaProfit)}</span></div>
      <div><span>Marjan profit</span><span>{fmtMAD(r.marjanProfit)}</span></div>
      <div><span>Gross profit</span><span>{fmtMAD(r.grossProfit)}</span></div>
      <div><span>Expenses</span><span>{fmtMAD(r.expenses)}</span></div>
      {r.tax > 0 && <div><span>Tax</span><span>{fmtMAD(r.tax)}</span></div>}
      <div style={{ fontWeight: 700 }}><span>Net</span><span className={r.net >= 0 ? 'np__pos' : 'np__neg'}>{signed(r.net)}</span></div>
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────
const NetProfitPage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore.token;

  const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(1); return fmt(d); });
  const [toDate, setToDate] = useState(() => fmt(new Date()));
  const [selectedStore, setSelectedStore] = useState('0');
  const [stores, setStores] = useState<IStoreResponse[]>([]);
  const [trigger, setTrigger] = useState(0);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [monthly, setMonthly] = useState<MonthRow[]>([]);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | undefined>();
  const [flash, setFlash] = useState<string | null>(null);
  const show = useCallback((msg: string) => { setFlash(msg); setTimeout(() => setFlash(null), 5000); }, []);

  // expense modal
  const [modal, setModal] = useState<null | { id?: string; form: ReturnType<typeof emptyForm> }>(null);
  const [saving, setSaving] = useState(false);
  // settings modal
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState<ProfitSettings | null>(null);

  useEffect(() => {
    (async () => {
      const r = await dispatch(actions.getStores, {}, token);
      if (Array.isArray(r)) setStores(r);
    })();
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, m, v] = await Promise.all([
      dispatch(actions.getProfitSummary, { fromDate: new Date(fromDate).toISOString(), toDate: new Date(toDate).toISOString(), storeId: selectedStore }, token),
      dispatch(actions.getProfitMonthly, {}, token, { months: 12, storeId: selectedStore }),
      dispatch(actions.getAutoPilotVerdict, {}, token),
    ]);   // "Today's estimate" removed (user 2026-10-16: not accurate)
    if (isErr(s)) show(`⚠ ${errMsg(s)}`); else { setSummary(s); setLastUpdated(new Date()); }
    if (Array.isArray(m)) setMonthly(m);
    if (v && !isErr(v) && v.status) setVerdict(v);
    setLoading(false);
  }, [fromDate, toDate, selectedStore, token, show]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [trigger, selectedStore]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const categories = summary?.categories || [];
  const catMap = useMemo(() => Object.fromEntries(categories.map(c => [c.key, c])), [categories]);
  const expenses = summary?.expenses.items || [];
  const forgotten = useMemo(
    () => categories.filter(c => c.key !== 'other' && !expenses.some(e => e.active && e.category === c.key)),
    [categories, expenses],
  );

  const waterfall = useMemo(() => {
    if (!summary) return [];
    const s = summary.sales, n = summary.net, mj = summary.marjan, cb = summary.combined;
    const steps: { name: string; delta: number; kind: 'total' | 'step'; hint?: string }[] = [
      { name: 'Customers paid', delta: cb.customerPaid, kind: 'total', hint: `${s.delivered} Jumia delivered + ${mj.orders} Marjan orders, at the marketplace price` },
    ];
    if (cb.channels.jumia) steps.push({ name: 'Jumia commission & shipping', delta: -s.jumiaFees, kind: 'step', hint: 'Already taken by Jumia before payout (from the order transactions)' });
    if (cb.channels.marjan) steps.push({ name: 'Marjan commission, services, refunds & gestures', delta: -mj.fees + mj.refunds + (mj.other || 0), kind: 'step', hint: `Commission ${fmtMAD(-mj.commissions)} · services ${fmtMAD(-mj.services)} · refunds ${fmtMAD(-mj.refunds)} · Marjane gestures/compensations ${fmtMAD(mj.other || 0)} (Geste commercial RT, Retour non vendable — credited by Marjane, counted as income)` });
    steps.push({ name: 'Your payout', delta: cb.payout, kind: 'total', hint: `Jumia ${fmtMAD(s.revenue)} + Marjan ${fmtMAD(mj.payout)}` });
    steps.push({ name: 'Product cost', delta: -cb.cogs, kind: 'step', hint: 'Buying price of sold units (both marketplaces)' });
    steps.push({ name: 'Transport / pack / storage', delta: -cb.logistics, kind: 'step', hint: 'Per-unit fees from product groups' });
    if (s.returnCost + s.failedCost > 0) steps.push({ name: 'Returns / failed', delta: -(s.returnCost + s.failedCost), kind: 'step', hint: `${s.returned} returned, ${s.failed} failed (Jumia)` });
    if (mj.unknownCostPayout > 0 || s.unknownCostPayout > 0) steps.push({ name: 'Unknown-cost SKUs', delta: -(mj.unknownCostPayout + s.unknownCostPayout), kind: 'step', hint: 'Payout of SKUs without a product group — excluded from profit until you associate them' });
    steps.push({ name: 'Gross profit', delta: cb.grossProfit, kind: 'total', hint: `Jumia ${fmtMAD(cb.jumiaProfit)} + Marjan ${fmtMAD(cb.marjanProfit)}` });
    for (const c of summary.expenses.byCategory) if (c.total > 0) steps.push({ name: `${c.icon} ${c.label}`, delta: -c.total, kind: 'step', hint: `${c.count} active expense${c.count === 1 ? '' : 's'}` });
    if (n.tax > 0) steps.push({ name: 'Tax', delta: -n.tax, kind: 'step', hint: `${summary.settings.taxRatePct}% of positive profit` });
    steps.push({ name: 'NET PROFIT', delta: n.afterTax, kind: 'total' });
    let run = 0;
    return steps.map(st => {
      const start = st.kind === 'total' ? 0 : run;
      const end = st.kind === 'total' ? st.delta : run + st.delta;
      run = end;
      const lo = Math.min(start, end), hi = Math.max(start, end);
      return { ...st, start, end, base: lo, value: hi - lo, color: st.kind === 'total' ? (end < 0 ? C.neg : C.total) : st.delta < 0 ? C.expenses : C.pos };
    });
  }, [summary]);

  // ── Expense CRUD ──────────────────────────────────────────────────────────
  const openAdd = (category = 'other') => setModal({ form: emptyForm(category) });
  const openEdit = (e: ExpenseItem) => setModal({
    id: e._id,
    form: {
      name: e.name, category: e.category, amount: String(e.amount), frequency: e.frequency,
      date: e.date ? e.date.slice(0, 10) : fmt(new Date()),
      startDate: e.startDate ? e.startDate.slice(0, 10) : fmt(new Date()),
      endDate: e.endDate ? e.endDate.slice(0, 10) : '', notes: e.notes || '', active: e.active,
    },
  });

  const saveExpense = async () => {
    if (!modal) return;
    const f = modal.form;
    if (!f.name.trim()) return show('⚠ Give the expense a name');
    if (!(parseFloat(f.amount) >= 0)) return show('⚠ Amount must be a number');
    setSaving(true);
    const body = {
      name: f.name.trim(), category: f.category, amount: parseFloat(f.amount), frequency: f.frequency,
      date: f.frequency === 'once' ? f.date : null, startDate: f.startDate || null, endDate: f.endDate || null,
      notes: f.notes || null, active: f.active,
    };
    const r = modal.id
      ? await dispatch(actions.updateExpense, body, token, { id: modal.id })
      : await dispatch(actions.createExpense, body, token);
    setSaving(false);
    if (isErr(r)) return show(`⚠ ${errMsg(r)}`);
    setModal(null);
    show(modal.id ? 'Expense updated' : 'Expense added');
    load();
  };

  const toggleExpense = async (e: ExpenseItem) => {
    const r = await dispatch(actions.updateExpense, { active: !e.active }, token, { id: e._id });
    if (isErr(r)) return show(`⚠ ${errMsg(r)}`);
    load();
  };

  const deleteExpense = async (e: ExpenseItem) => {
    if (!window.confirm(`Delete "${e.name}"? This cannot be undone.`)) return;
    const r = await dispatch(actions.deleteExpense, {}, token, { id: e._id });
    if (isErr(r)) return show(`⚠ ${errMsg(r)}`);
    show('Expense deleted');
    load();
  };

  const openSettings = () => { if (summary) { setSettingsForm({ ...summary.settings }); setSettingsOpen(true); } };
  const saveSettings = async () => {
    if (!settingsForm) return;
    setSaving(true);
    const r = await dispatch(actions.saveProfitSettings, settingsForm, token);
    setSaving(false);
    if (isErr(r)) return show(`⚠ ${errMsg(r)}`);
    setSettingsOpen(false); show('Settings saved'); load();
  };

  const s = summary?.sales, n = summary?.net, ex = summary?.expenses, mj = summary?.marjan, cb = summary?.combined;
  // the colour and the big number follow the EXPECTED result while orders are still on the way
  const ex2 = n && !n.final && n.expected && n.expected.orders > 0 ? n.expected : null;
  const shownNet = n ? (ex2 ? ex2.afterTax : n.afterTax) : 0;
  const shownDaily = n ? (ex2 ? ex2.dailyNet : n.dailyNet) : 0;
  const heroCls = !n ? 'np__hero--zero' : shownNet > 0 ? 'np__hero--pos' : shownNet < 0 ? 'np__hero--neg' : 'np__hero--zero';
  const days = summary?.period.days || 1;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="np">
      <div className="np__head">
        <div className="np__title-block">
          <div className="np__title-icon"><Wallet size={16} color="#fff" /></div>
          <div>
            <div className="np__title">My Net Profit</div>
            <div className="np__sub">Jumia + Marjan in one place: what is really left after salaries, rent, electricity, internet and every other bill</div>
          </div>
        </div>
        <select className="np__select" value={selectedStore} onChange={e => setSelectedStore(e.target.value)}>
          <option value="0">All channels (Jumia + Marjan)</option>
          <option value="jumia">Jumia — all stores</option>
          <option value="marjan">Marjan — all stores</option>
          <optgroup label="Jumia store">
            {stores.map(st => <option key={st._id} value={st._id}>{st.name}</option>)}
          </optgroup>
        </select>
        <DateRangeFilter
          fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate}
          onApply={() => setTrigger(t => t + 1)} loading={loading} lastUpdated={lastUpdated} initialPreset="This month"
        />
        <button className="np__btn" onClick={() => setTrigger(t => t + 1)} disabled={loading} title="Refresh">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
        <button className="np__btn" onClick={openSettings} disabled={!summary}><Settings size={13} /> Assumptions</button>
      </div>

      <div className="np__body">
        {summary?.warnings.map(w => (
          <div key={w.type} className={`np__warn${w.type === 'no-expenses' ? ' np__warn--info' : ''}`}>
            {w.type === 'no-expenses' ? <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />}
            <span>{w.text}</span>
          </div>
        ))}

        {/* Break-even energy bar — the month's progress first, the detail below */}
        {monthly.length > 0 && <BreakEvenBar months={monthly} />}

        {/* Hero */}
        <div className={`np__hero ${heroCls}`}>
          <div>
            <div className="np__hero-label">{ex2 ? 'Expected net profit' : 'Net profit'} · {fromDate} → {toDate} · {days} day{days === 1 ? '' : 's'}
              {n && (n.final ? <span className="np__final np__final--ok">final ✓</span> : <span className="np__final">⏳ still moving · final {fmtDay(n.finalOn)}</span>)}
            </div>
            <div className="np__hero-value">{loading && !summary ? '…' : n ? signed(shownNet) : '—'}</div>
            {ex2 && n && <div className="np__hero-line"><b>Confirmed so far: {signed(n.afterTax)}</b> (delivered orders only) · <b>+{fmtMAD(ex2.onTheWay)}</b> expected from {ex2.orders} orders still on the way ({ex2.jumia?.orders || 0} Jumia · {ex2.marjan?.orders || 0} Marjan)</div>}
            {n && s && (
              <div className="np__hero-line">
                {shownNet >= 0
                  ? `That is ${fmtMAD(shownDaily)} per day (≈ ${fmtMAD(n.monthlyRunRate)} / month at this pace) and ${fmtMAD(n.perDeliveredOrder)} per order across ${(cb?.orders || 0).toLocaleString()} Jumia + Marjan orders.`
                  : `${ex2 ? 'Once they settle you would be losing' : 'You are losing'} ${fmtMAD(Math.abs(shownDaily))} per day. ${n.breakEvenOrders != null ? `You needed ${n.breakEvenOrders.toLocaleString()} orders to break even — you had ${(cb?.orders || 0).toLocaleString()}.` : 'Gross profit per order is not positive, so no volume fixes this — check prices and product costs.'}`}
              </div>
            )}
          </div>
          {s && n && ex && (
            <div className="np__hero-eq">
              {cb?.channels.jumia && <div className="np__hero-eq-row"><span>Jumia profit (same as Smart Analytics)</span><span>{fmtMAD(s.grossProfitSA)}</span></div>}
              {cb?.channels.marjan && <div className="np__hero-eq-row"><span>+ Marjan profit (payouts − product cost)</span><span>{fmtMAD(cb.marjanProfit)}</span></div>}
              {s.returnCost + s.failedCost > 0 && <div className="np__hero-eq-row"><span>− Returns / failed deliveries</span><span>{fmtMAD(s.returnCost + s.failedCost)}</span></div>}
              <div className="np__hero-eq-row"><span>− Business expenses ({ex.items.filter(i => i.active).length})</span><span>{fmtMAD(ex.total)}</span></div>
              {n.tax > 0 && <div className="np__hero-eq-row"><span>− Tax ({summary!.settings.taxRatePct}%)</span><span>{fmtMAD(n.tax)}</span></div>}
              <div className="np__hero-eq-row"><span>= {ex2 ? 'Confirmed net' : 'Net profit'}</span><span>{signed(n.afterTax)}</span></div>
              {ex2 && <>
                {(ex2.jumia?.orders || 0) > 0 && <div className="np__hero-eq-row"><span>+ Jumia on the way: {ex2.jumia!.orders} orders worth {fmtMAD(ex2.jumia!.value)} ({ex2.jumia!.shipped} shipped, {ex2.jumia!.notShipped} not shipped yet) → ≈ {Math.round(ex2.jumia!.expectedDeliveries)} delivered ({Math.round(ex2.jumia!.rate)}% usually are)</span><span>+{fmtMAD(ex2.jumia!.profit)}</span></div>}
                {(ex2.marjan?.orders || 0) > 0 && <div className="np__hero-eq-row"><span>+ Marjan on the way: {ex2.marjan!.orders} orders worth {fmtMAD(ex2.marjan!.value)} ({ex2.marjan!.atHub} at Marjane's hub, {ex2.marjan!.notAtHub} not handed over yet) → ≈ {Math.round(ex2.marjan!.expectedDeliveries)} delivered</span><span>+{fmtMAD(ex2.marjan!.profit)}</span></div>}
                <div className="np__hero-eq-row"><span>= Expected net</span><span>{signed(ex2.afterTax)}</span></div>
              </>}
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="np__kpis">
          <KpiCard label="Payout (both)" value={fmtMAD(cb?.payout || 0)} sub={`Jumia ${fmtMAD(s?.revenue || 0)} · Marjan ${fmtMAD(mj?.payout || 0)} · fees already taken ${fmtMAD(cb?.fees || 0)}`} icon={Coins} color="blue" loading={loading && !summary} />
          <KpiCard label="Jumia profit" value={fmtMAD(s?.grossProfit || 0)} sub={`${(s?.delivered || 0).toLocaleString()} delivered · before your expenses`} icon={TrendingUp} color={(s?.grossProfit || 0) >= 0 ? 'green' : 'red'} loading={loading && !summary} />
          <KpiCard label="Marjan profit" value={fmtMAD(mj?.profit || 0)} sub={mj && mj.stores > 0 ? `${mj.orders.toLocaleString()} orders · ${mj.unknownCostSkus ? `${mj.unknownCostSkus} SKUs without cost excluded` : 'payouts − product cost'}` : 'no Marjan store connected'} icon={StoreIcon} color={(mj?.profit || 0) >= 0 ? 'purple' : 'red'} loading={loading && !summary} />
          <KpiCard label="Expenses" value={fmtMAD(ex?.total || 0)} sub={`${fmtMAD(ex?.monthlyRunRate || 0)} / month run-rate`} icon={Receipt} color="red" loading={loading && !summary} />
          <KpiCard label="Net margin" value={`${(n?.marginPct || 0).toFixed(1)}%`} sub={`${fmtMAD(n?.perDeliveredOrder || 0)} net / order`} icon={Percent} color={(n?.marginPct || 0) >= 10 ? 'green' : (n?.marginPct || 0) >= 0 ? 'orange' : 'red'} trend={(n?.marginPct || 0) >= 0 ? 'up' : 'down'} loading={loading && !summary} />
          <KpiCard label="Break-even" value={n?.breakEvenOrders != null ? n.breakEvenOrders.toLocaleString() : '—'} sub={n?.breakEvenOrders != null ? `orders to cover expenses · you had ${(cb?.orders || 0).toLocaleString()}` : 'gross / order must be > 0'} icon={Target} color={n?.breakEvenOrders != null && (cb?.orders || 0) >= n.breakEvenOrders ? 'green' : 'purple'} loading={loading && !summary} />
        </div>

        {/* Did Auto-Pilot pay? */}
        {verdict && verdict.status !== 'no-autopilot' && (() => {
          const st = VERDICT_STYLE[verdict.status];
          const fmtDate = (d?: string) => (d ? d.slice(0, 10) : '');
          return (
            <div className="np__card" style={{ marginBottom: '1rem', borderLeft: `4px solid ${st.fg}` }}>
              <div className="np__card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                🤖 Did Auto-Pilot pay?
                <span style={{ background: st.bg, color: st.fg, borderRadius: 999, padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: 700 }}>{st.icon} {st.label}</span>
                {verdict.enrolledSkus != null && <span style={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: 500 }}>{verdict.enrolledSkus.toLocaleString()} enrolled SKUs · Auto-Pilot since {fmtDate(verdict.milestones?.autoPilotStart)} · profit check since {fmtDate(verdict.milestones?.profitCheckSince)}</span>}
              </div>
              <div className="np__card-sub" style={{ fontSize: '0.9rem', color: '#111827', fontWeight: 600 }}>{verdict.headline}</div>
              <div className="np__table-wrap">
                <table className="np__table">
                  <thead><tr><th>Period</th><th className="num">Days</th><th className="num">Orders / day</th><th className="num">Profit / unit</th><th className="num">Profit / day</th><th className="num">Delivered</th><th className="num">Still open</th></tr></thead>
                  <tbody>
                    {verdict.periods.map(p => (
                      <tr key={p.key} style={p.key === 'new' ? { fontWeight: 700 } : undefined}>
                        <td>{p.label}<div style={{ fontSize: '0.66rem', color: '#6B7280', fontWeight: 400 }}>{fmtDate(p.from)} → {fmtDate(p.to)}</div></td>
                        <td className="num">{Math.round(p.days)}</td>
                        <td className="num">{p.ordersPerDay}</td>
                        <td className="num">{p.profitPerUnit != null ? `${p.profitPerUnit} MAD` : '—'}</td>
                        <td className="num">{p.profitPerDay.toLocaleString()} MAD</td>
                        <td className="num">{p.deliveredRatePct != null ? `${p.deliveredRatePct}%` : '—'}</td>
                        <td className="num">{p.open || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {verdict.detail && <p style={{ fontSize: '0.8rem', color: '#374151', margin: '0.7rem 0 0.3rem' }}>{verdict.detail}</p>}
              <p style={{ fontSize: '0.85rem', margin: '0.4rem 0', padding: '0.6rem 0.8rem', background: st.bg, color: st.fg, borderRadius: 10 }}><b>What to do:</b> {verdict.recommendation}</p>
              {(verdict.notes || []).map((n, i) => <p key={i} style={{ fontSize: '0.72rem', color: '#6B7280', margin: '0.2rem 0' }}>ℹ️ {n}</p>)}
              <p style={{ fontSize: '0.72rem', color: '#6B7280', margin: '0.4rem 0 0' }}>Profit = Jumia payout − product & logistics costs, on order date, for enrolled SKUs only. This verdict is also sent to the team Telegram every Monday morning.</p>
            </div>
          );
        })()}

        <div className="np__grid2">
          {/* Waterfall */}
          <div className="np__card">
            <div className="np__card-title">Where the money goes</div>
            <div className="np__card-sub">From what customers paid on Jumia and Marjan, through the payouts, down to what stays in your pocket · {fromDate} → {toDate}</div>
            {waterfall.length ? (
              <ResponsiveContainer width="100%" height={330}>
                <BarChart data={waterfall} margin={{ top: 8, right: 8, left: 0, bottom: 60 }} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} interval={0} angle={-35} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={fmtK} width={48} />
                  <Tooltip content={<WaterfallTip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                  <ReferenceLine y={0} stroke="#9CA3AF" />
                  <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
                  <Bar dataKey="value" stackId="w" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {waterfall.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="np__empty">No data for this period</div>}
            <div className="np__legend">
              <span><i style={{ background: C.total }} /> Totals</span>
              <span><i style={{ background: C.expenses }} /> Deductions</span>
              <span><i style={{ background: C.neg }} /> Negative total</span>
            </div>
          </div>

          {/* Category breakdown + stores */}
          <div className="np__card">
            <div className="np__card-title">Expenses by category</div>
            <div className="np__card-sub">Prorated to this period · {fmtMAD(ex?.total || 0)} total</div>
            {ex && ex.byCategory.filter(c => c.total > 0).length ? (
              <div className="np__cats">
                {ex.byCategory.filter(c => c.total > 0).map(c => (
                  <div key={c.category} className="np__cat-row">
                    <span>{c.icon} {c.label}</span>
                    <div className="np__cat-bar"><i style={{ width: `${Math.max(2, c.total / ex.total * 100)}%` }} /></div>
                    <span className="num" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMAD(c.total)}</span>
                  </div>
                ))}
              </div>
            ) : <div className="np__empty">No expenses in this period yet.</div>}

            {['0', 'jumia', 'marjan'].includes(selectedStore) && summary && summary.byStore.length > 1 && (
              <>
                <div className="np__card-title" style={{ marginTop: '1.1rem' }}><StoreIcon size={14} /> Gross profit by store</div>
                <div className="np__card-sub">Shared expenses are not split per store — this is before expenses</div>
                <div className="np__table-wrap">
                  <table className="np__table">
                    <thead><tr><th>Store</th><th className="num">Orders</th><th className="num">Payout</th><th className="num">Gross profit</th></tr></thead>
                    <tbody>
                      {summary.byStore.map(st => (
                        <tr key={`${st.channel}-${st.storeId}`}>
                          <td><span className="np__cat" style={st.channel === 'marjan' ? { background: '#EDE9FE', color: '#5B21B6' } : { background: '#FFEDD5', color: '#9A3412' }}>{st.channel === 'marjan' ? 'Marjan' : 'Jumia'}</span> {st.name}</td>
                          <td className="num">{st.delivered.toLocaleString()}</td>
                          <td className="num">{fmtMAD(st.revenue)}</td>
                          <td className={`num ${st.grossProfit >= 0 ? 'np__pos' : 'np__neg'}`}>{fmtMAD(st.grossProfit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Monthly trend */}
        <div className="np__card">
          <div className="np__card-title">Last 12 months</div>
          <div className="np__card-sub">Jumia and Marjan profit vs. your expenses, and the net that remains · current month is month-to-date with expenses prorated</div>
          {monthly.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={fmtK} width={48} />
                <Tooltip content={<MonthlyTip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#9CA3AF" />
                <Bar dataKey="jumiaProfit" name="Jumia profit" stackId="p" fill={C.gross} isAnimationActive={false} />
                <Bar dataKey="marjanProfit" name="Marjan profit" stackId="p" fill={C.marjan} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="expenses" name="Expenses" fill={C.expenses} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Line dataKey="net" name="Net profit" stroke={C.ink} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div className="np__empty">Loading…</div>}
        </div>

        {/* Expenses manager */}
        <div className="np__card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ marginRight: 'auto' }}>
              <div className="np__card-title">Your expenses</div>
              <div className="np__card-sub" style={{ marginBottom: 0 }}>Recurring expenses are automatically prorated to whatever period you look at. Monthly run-rate: <b>{fmtMAD(ex?.monthlyRunRate || 0)}</b></div>
            </div>
            <button className="np__btn np__btn--primary" onClick={() => openAdd()}><Plus size={13} /> Add expense</button>
          </div>

          {forgotten.length > 0 && (
            <div style={{ margin: '0.9rem 0 0.4rem' }}>
              <div className="np__muted" style={{ marginBottom: '0.4rem' }}>💡 Did you forget one of these? Click to add it:</div>
              <div className="np__forgot">
                {forgotten.map(c => (
                  <button key={c.key} className="np__chip" title={c.hint} onClick={() => openAdd(c.key)}>{c.icon} {c.label}</button>
                ))}
              </div>
            </div>
          )}

          <div className="np__table-wrap" style={{ marginTop: '0.9rem' }}>
            {expenses.length ? (
              <table className="np__table">
                <thead>
                  <tr>
                    <th>Expense</th><th>Category</th><th className="num">Amount</th><th className="num">≈ / month</th>
                    <th className="num">This period</th><th>Since</th><th style={{ width: 96 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(e => (
                    <tr key={e._id} className={e.active ? '' : 'off'}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{e.name}</div>
                        {e.notes && <div className="np__muted">{e.notes}</div>}
                      </td>
                      <td><span className="np__cat">{catMap[e.category]?.icon} {catMap[e.category]?.label || e.category}</span></td>
                      <td className="num">{fmtMAD(e.amount)} <span className="np__freq">{FREQ_LABEL[e.frequency]}</span></td>
                      <td className="num">{e.frequency === 'once' ? '—' : fmtMAD(e.monthlyEquivalent)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{fmtMAD(e.costInPeriod)}</td>
                      <td className="np__muted">
                        {e.frequency === 'once' ? (e.date || '').slice(0, 10) : (e.startDate || '').slice(0, 10)}
                        {e.endDate ? ` → ${e.endDate.slice(0, 10)}` : ''}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button className="np__icon-btn" title={e.active ? 'Pause (keep but ignore)' : 'Activate'} onClick={() => toggleExpense(e)}><Power size={14} color={e.active ? '#059669' : '#9CA3AF'} /></button>
                        <button className="np__icon-btn" title="Edit" onClick={() => openEdit(e)}><Pencil size={14} /></button>
                        <button className="np__icon-btn" title="Delete" onClick={() => deleteExpense(e)}><Trash2 size={14} color="#B91C1C" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="np__empty">No expenses yet. Start with your team's salaries and the rent — they are usually the biggest.</div>
            )}
          </div>
        </div>
      </div>

      {/* Expense modal */}
      {modal && (
        <div className="np__overlay" onClick={() => !saving && setModal(null)}>
          <div className="np__modal" onClick={e => e.stopPropagation()}>
            <h3>{modal.id ? 'Edit expense' : 'Add expense'}</h3>
            <div className="np__form">
              <div className="np__field full">
                <label>Name</label>
                <input autoFocus value={modal.form.name} placeholder="e.g. Salary — Yassine, Warehouse rent, Orange fiber" onChange={e => setModal({ ...modal, form: { ...modal.form, name: e.target.value } })} />
              </div>
              <div className="np__field">
                <label>Category</label>
                <select value={modal.form.category} onChange={e => setModal({ ...modal, form: { ...modal.form, category: e.target.value } })}>
                  {categories.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
                </select>
                {catMap[modal.form.category] && <div className="help">{catMap[modal.form.category].hint}</div>}
              </div>
              <div className="np__field">
                <label>Amount (MAD)</label>
                <input type="number" min={0} step="0.01" value={modal.form.amount} onChange={e => setModal({ ...modal, form: { ...modal.form, amount: e.target.value } })} />
              </div>
              <div className="np__field">
                <label>How often</label>
                <select value={modal.form.frequency} onChange={e => setModal({ ...modal, form: { ...modal.form, frequency: e.target.value as Frequency } })}>
                  <option value="monthly">Every month</option>
                  <option value="weekly">Every week</option>
                  <option value="daily">Every day</option>
                  <option value="yearly">Every year</option>
                  <option value="once">One time</option>
                </select>
              </div>
              {modal.form.frequency === 'once' ? (
                <div className="np__field">
                  <label>Date paid</label>
                  <input type="date" value={modal.form.date} onChange={e => setModal({ ...modal, form: { ...modal.form, date: e.target.value } })} />
                </div>
              ) : (
                <>
                  <div className="np__field">
                    <label>Starts</label>
                    <input type="date" value={modal.form.startDate} onChange={e => setModal({ ...modal, form: { ...modal.form, startDate: e.target.value } })} />
                    <div className="help">Counted from this date — set it in the past if you have paid it for a while</div>
                  </div>
                  <div className="np__field">
                    <label>Ends (optional)</label>
                    <input type="date" value={modal.form.endDate} onChange={e => setModal({ ...modal, form: { ...modal.form, endDate: e.target.value } })} />
                    <div className="help">Leave empty if it is still running</div>
                  </div>
                </>
              )}
              <div className="np__field full">
                <label>Notes (optional)</label>
                <textarea value={modal.form.notes} onChange={e => setModal({ ...modal, form: { ...modal.form, notes: e.target.value } })} />
              </div>
              <label className="np__toggle full">
                <input type="checkbox" checked={modal.form.active} onChange={e => setModal({ ...modal, form: { ...modal.form, active: e.target.checked } })} />
                Active (included in the calculation)
              </label>
            </div>
            <div className="np__modal-actions">
              <button className="np__btn" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="np__btn np__btn--primary" onClick={saveExpense} disabled={saving}>{saving ? 'Saving…' : modal.id ? 'Save changes' : 'Add expense'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {settingsOpen && settingsForm && (
        <div className="np__overlay" onClick={() => !saving && setSettingsOpen(false)}>
          <div className="np__modal" onClick={e => e.stopPropagation()}>
            <h3>Assumptions</h3>
            <div className="np__form">
              <div className="np__muted full">
                Order prices from Jumia are already the net payout (commission and shipping contribution deducted), so nothing is estimated for Jumia fees — the page starts from the same profit as Smart Analytics.
              </div>
              <div className="np__field">
                <label>Cost per returned order (MAD)</label>
                <input type="number" min={0} step="1" value={settingsForm.costPerReturn} onChange={e => setSettingsForm({ ...settingsForm, costPerReturn: parseFloat(e.target.value) || 0 })} />
                <div className="help">Return shipping, repackaging, damaged packaging. 0 to ignore.</div>
              </div>
              <div className="np__field">
                <label>Cost per failed delivery (MAD)</label>
                <input type="number" min={0} step="1" value={settingsForm.costPerFailed} onChange={e => setSettingsForm({ ...settingsForm, costPerFailed: parseFloat(e.target.value) || 0 })} />
                <div className="help">What Jumia charges you when a delivery fails. 0 to ignore.</div>
              </div>
              <div className="np__field">
                <label>Tax on profit %</label>
                <input type="number" min={0} max={100} step="0.5" value={settingsForm.taxRatePct} onChange={e => setSettingsForm({ ...settingsForm, taxRatePct: parseFloat(e.target.value) || 0 })} />
                <div className="help">Rough IS estimate applied to positive net profit. 0 to ignore.</div>
              </div>
              <div className="np__field">
                <label>Date basis</label>
                <select value={settingsForm.dateBasis} onChange={e => setSettingsForm({ ...settingsForm, dateBasis: e.target.value as 'date' | 'order' })}>
                  <option value="date">Status date (same as Smart Analytics)</option>
                  <option value="order">Order date</option>
                </select>
              </div>
            </div>
            <div className="np__modal-actions">
              <button className="np__btn" onClick={() => setSettingsOpen(false)} disabled={saving}>Cancel</button>
              <button className="np__btn np__btn--primary" onClick={saveSettings} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {flash && <div className={`np__flash${flash.startsWith('⚠') ? ' np__flash--bad' : ''}`}>{flash}</div>}
    </div>
  );
};

export default NetProfitPage;
